import { SocialExportYoutubePlaylistDto } from '@mi-front/api-v2';
import { AutocompleteElement, useFormContext, useWatch } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { YoutubePlaylistListItem } from './YoutubePlaylistListItem';
import { YoutubePlaylistTag } from './YoutubePlaylistTag';

type Props = {
  playlists: SocialExportYoutubePlaylistDto[];
  isLoading: boolean;
  maxSelected: number;
  helperText?: React.ReactNode;
  disabled?: boolean;
};

/**
 * Autocomplete component for selecting YouTube playlists.
 * Handles the autocomplete UI logic including option rendering, tags, and disabled states.
 * Manages the max selected limit internally by watching the form value.
 */
export function YoutubePlaylistsAutocomplete({
  playlists,
  isLoading,
  maxSelected,
  helperText,
  disabled = false,
}: Props) {
  const { control } = useFormContext();
  const { $t } = useIntl();
  const selectedPlaylists = useWatch({ control, name: 'playlistsIds' }) as
    | SocialExportYoutubePlaylistDto[]
    | null;
  const selectedCount = selectedPlaylists?.length ?? 0;
  const isMaxReached = selectedCount >= maxSelected;

  return (
    <AutocompleteElement
      name="playlistsIds"
      control={control}
      options={playlists}
      multiple
      loading={isLoading}
      label={$t({
        id: 'socialExport.youtube.playlist.label',
        defaultMessage: 'Playlists',
      })}
      textFieldProps={{
        fullWidth: true,
        helperText: helperText,
        disabled,
      }}
      autocompleteProps={{
        fullWidth: true,
        disabled,
        getOptionLabel: (option: SocialExportYoutubePlaylistDto) => {
          if (typeof option === 'string') return option;
          return option.title;
        },
        isOptionEqualToValue: (
          option: SocialExportYoutubePlaylistDto,
          value: SocialExportYoutubePlaylistDto,
        ) => {
          return option.id === value?.id;
        },
        getOptionDisabled: (option: SocialExportYoutubePlaylistDto) => {
          // Disable option if max limit reached and this option is not already selected
          if (isMaxReached) {
            const isSelected = selectedPlaylists?.some((p) => p.id === option.id);
            return !isSelected;
          }
          return false;
        },
        renderOption: (props, option: SocialExportYoutubePlaylistDto) => (
          <YoutubePlaylistListItem {...props} playlist={option} key={option.id} />
        ),
        renderTags: (value: SocialExportYoutubePlaylistDto[], getTagProps) =>
          value.map((option: SocialExportYoutubePlaylistDto, index: number) => {
            const { key, ...tagProps } = getTagProps({ index });
            return <YoutubePlaylistTag key={key} playlist={option} {...tagProps} />;
          }),
        noOptionsText: $t({
          id: 'socialExport.youtube.playlist.noOptions',
          defaultMessage: 'No hay playlists disponibles',
        }),
      }}
    />
  );
}
